import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { Plus, ShoppingCart, Package } from 'lucide-react';

const PurchasesPage = () => {
    const { api } = useAuth();
    const [purchases, setPurchases] = useState([]);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [formData, setFormData] = useState({
        product_id: '',
        quantity: '',
        unit_price: '',
        supplier: '',
        notes: ''
    });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [purchasesRes, productsRes] = await Promise.all([
                api().get('/purchases'),
                api().get('/products')
            ]);
            setPurchases(purchasesRes.data);
            setProducts(productsRes.data);
        } catch (error) {
            toast.error('Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    };

    const handleProductChange = (productId) => {
        const product = products.find(p => p.id === productId);
        setFormData({
            ...formData,
            product_id: productId,
            unit_price: product ? product.purchase_price.toString() : ''
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            await api().post('/purchases', {
                ...formData,
                quantity: parseFloat(formData.quantity),
                unit_price: parseFloat(formData.unit_price)
            });
            toast.success('Achat enregistré - Stock mis à jour');
            setDialogOpen(false);
            setFormData({ product_id: '', quantity: '', unit_price: '', supplier: '', notes: '' });
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur');
        }
    };

    const selectedProduct = products.find(p => p.id === formData.product_id);

    if (loading) {
        return (
            <div className="space-y-6 animate-pulse">
                <div className="h-8 bg-muted rounded w-48"></div>
                <div className="h-96 bg-muted rounded-xl"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="purchases-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="font-display text-3xl font-bold text-foreground tracking-tight">
                        Achats / Entrées
                    </h1>
                    <p className="text-muted-foreground mt-1">
                        Enregistrez vos achats et entrées de stock
                    </p>
                </div>
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogTrigger asChild>
                        <Button className="bg-accent hover:bg-accent/90 btn-press" data-testid="add-purchase-btn">
                            <Plus className="w-4 h-4 mr-2" />
                            Nouvel achat
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-[500px]">
                        <DialogHeader>
                            <DialogTitle className="font-display">Enregistrer un achat</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                            <div className="space-y-2">
                                <Label htmlFor="product">Produit</Label>
                                <Select 
                                    value={formData.product_id} 
                                    onValueChange={handleProductChange}
                                    required
                                >
                                    <SelectTrigger data-testid="purchase-product-select">
                                        <SelectValue placeholder="Sélectionner un produit..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {products.map((product) => (
                                            <SelectItem key={product.id} value={product.id}>
                                                {product.name} ({product.unit})
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            {selectedProduct && (
                                <div className="p-3 bg-muted/50 rounded-lg text-sm">
                                    <div className="flex justify-between">
                                        <span className="text-muted-foreground">Stock actuel:</span>
                                        <span className="font-mono font-semibold">
                                            {selectedProduct.stock_quantity} {selectedProduct.unit}
                                        </span>
                                    </div>
                                </div>
                            )}

                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="quantity">Quantité</Label>
                                    <Input
                                        id="quantity"
                                        type="number"
                                        step="0.01"
                                        min="0.01"
                                        value={formData.quantity}
                                        onChange={(e) => setFormData({...formData, quantity: e.target.value})}
                                        placeholder="0"
                                        required
                                        data-testid="purchase-quantity-input"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="unit_price">Prix unitaire (CFA)</Label>
                                    <Input
                                        id="unit_price"
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={formData.unit_price}
                                        onChange={(e) => setFormData({...formData, unit_price: e.target.value})}
                                        placeholder="0.00"
                                        required
                                        data-testid="purchase-price-input"
                                    />
                                </div>
                            </div>

                            {formData.quantity && formData.unit_price && (
                                <div className="p-3 bg-accent/10 rounded-lg">
                                    <div className="flex justify-between text-sm">
                                        <span className="text-muted-foreground">Total:</span>
                                        <span className="font-mono font-bold text-accent">
                                            {(parseFloat(formData.quantity) * parseFloat(formData.unit_price)).toFixed(2)} CFA
                                        </span>
                                    </div>
                                </div>
                            )}

                            <div className="space-y-2">
                                <Label htmlFor="supplier">Fournisseur (optionnel)</Label>
                                <Input
                                    id="supplier"
                                    value={formData.supplier}
                                    onChange={(e) => setFormData({...formData, supplier: e.target.value})}
                                    placeholder="Nom du fournisseur"
                                    data-testid="purchase-supplier-input"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="notes">Notes (optionnel)</Label>
                                <Textarea
                                    id="notes"
                                    value={formData.notes}
                                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                                    placeholder="Notes sur cet achat..."
                                    rows={2}
                                    data-testid="purchase-notes-input"
                                />
                            </div>

                            <div className="flex justify-end gap-3 pt-4">
                                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                                    Annuler
                                </Button>
                                <Button type="submit" className="bg-accent hover:bg-accent/90" data-testid="purchase-submit-btn">
                                    Enregistrer
                                </Button>
                            </div>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            {/* Purchases Table */}
            <Card>
                <div className="rounded-xl border border-border overflow-hidden">
                    <Table>
                        <TableHeader>
                            <TableRow className="bg-muted/30">
                                <TableHead className="font-medium">Date</TableHead>
                                <TableHead className="font-medium">Produit</TableHead>
                                <TableHead className="font-medium">Fournisseur</TableHead>
                                <TableHead className="font-medium text-right">Quantité</TableHead>
                                <TableHead className="font-medium text-right">Prix Unit.</TableHead>
                                <TableHead className="font-medium text-right">Total</TableHead>
                                <TableHead className="font-medium">Par</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {purchases.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} className="text-center py-12">
                                        <ShoppingCart className="w-12 h-12 mx-auto text-muted-foreground/50 mb-3" />
                                        <p className="text-muted-foreground">Aucun achat enregistré</p>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                purchases.map((purchase) => (
                                    <TableRow key={purchase.id} className="hover:bg-muted/20" data-testid={`purchase-row-${purchase.id}`}>
                                        <TableCell className="font-mono text-sm">
                                            {new Date(purchase.created_at).toLocaleDateString('fr-FR')}
                                        </TableCell>
                                        <TableCell className="font-medium">{purchase.product_name}</TableCell>
                                        <TableCell className="text-muted-foreground">
                                            {purchase.supplier || '-'}
                                        </TableCell>
                                        <TableCell className="text-right font-mono">
                                            {purchase.quantity}
                                        </TableCell>
                                        <TableCell className="text-right font-mono">
                                            {purchase.unit_price.toFixed(2)} CFA
                                        </TableCell>
                                        <TableCell className="text-right font-mono font-semibold text-accent">
                                            {purchase.total_price.toFixed(2)} CFA
                                        </TableCell>
                                        <TableCell className="text-muted-foreground text-sm">
                                            {purchase.created_by_name}
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </div>
            </Card>
        </div>
    );
};

export default PurchasesPage;
